/**
 * Dashboard com 4 KPI cards + tabela top-5 das Telas (TH-54206 / M1.E4.S2.3).
 *
 * Só dado real: sem TV, estado vazio. O dado de demonstração (lista de TVs e
 * KPIs fixos de campanhas/SKUs) aparecia em produção para cliente sem TV e
 * saiu — PSHWO-67349.
 */

import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { StatusPill, type StatusTone } from "@/components/ui/StatusPill";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

interface TVRow {
  id: string;
  store_id: string;
  name: string;
  area_id: string | null;
  app_version: string | null;
  last_seen_at: string | null;
  store_code: string | null;
  store_name: string | null;
  tenant_name: string | null;
  content_expires_at: string | null;
  has_active_content: boolean;
}

type TVListResponse = TVRow[];

type TVStatus = "online" | "syncing" | "offline";


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

function formatHHMM(d: Date): string {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function freshnessLabel(updatedAt: Date): string {
  const secs = Math.floor((Date.now() - updatedAt.getTime()) / 1000);
  if (secs < 60) return `atualizado há ${secs}s`;
  const mins = Math.floor(secs / 60);
  return `atualizado há ${mins}min`;
}

// TODO M3: status real — endpoint atual só devolve `last_seen_at`.
function deriveStatus(lastSeenAt: string | null): TVStatus {
  if (!lastSeenAt) return "offline";
  const last = new Date(lastSeenAt).getTime();
  if (Number.isNaN(last)) return "offline";
  const ageMs = Date.now() - last;
  if (ageMs < 5 * 60_000) return "online";
  return "offline";
}

// GET /tvs não devolve total: pede o máximo da API (100) e conta o que veio.
// A tabela mostra só as 5 primeiras.
const TVS_PER_PAGE = 100;
const TABLE_ROWS = 5;

async function fetchTVs(): Promise<TVListResponse | null> {
  const res = await fetch(`/api/tvs?per_page=${TVS_PER_PAGE}`, { credentials: "include" });
  if (!res.ok) return null;
  return (await res.json()) as TVListResponse;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DashboardPage() {
  const { user } = useAuth();
  const tvsQ = useQuery({
    queryKey: ["dashboard", "tvs"],
    queryFn: fetchTVs,
    staleTime: 30_000,
    retry: false,
  });

  const firstName = user?.name?.trim().split(/\s+/)[0];
  const title = firstName ? `${greeting()}, ${firstName}` : greeting();

  const realTvs = tvsQ.data ?? [];
  const tvsAvailable = realTvs.length > 0;
  const updatedAt = tvsQ.dataUpdatedAt ? new Date(tvsQ.dataUpdatedAt) : new Date();

  const total = realTvs.length;
  const onlineCount = realTvs.filter(
    (tv) => deriveStatus(tv.last_seen_at) === "online",
  ).length;
  // Último sinal recebido de qualquer TV — e não a hora em que o painel buscou.
  const lastSignal = realTvs.reduce<Date | null>((acc, tv) => {
    if (!tv.last_seen_at) return acc;
    const d = new Date(tv.last_seen_at);
    if (Number.isNaN(d.getTime())) return acc;
    return acc === null || d > acc ? d : acc;
  }, null);
  const onlinePct =
    total > 0 ? Math.round((onlineCount / total) * 100) : null;
  const onlineDeltaColor: "success" | "muted" | "warn" =
    onlinePct === null
      ? "muted"
      : onlinePct >= 80
        ? "success"
        : onlinePct >= 50
          ? "muted"
          : "warn";

  const actions = (
    <>
      <a
        href="/imports"
        className="inline-flex h-8 items-center justify-center rounded-md border border-[var(--color-border-strong)] bg-transparent px-3 text-xs font-medium transition-colors hover:bg-[var(--color-secondary)]"
      >
        Importar planilha
      </a>
      <Button size="sm" disabled title="Disponível em M5">
        + Nova campanha
      </Button>
    </>
  );

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / VISÃO GERAL"
        title={title}
        actions={actions}
      >
        <div className="flex flex-col gap-5">
          {/* Grid 1×4 de KPIs */}
          <div className="grid grid-cols-1 gap-[14px] sm:grid-cols-2">
            <KPICard
              label="Telas no ar"
              value={`${onlineCount}/${total}`}
              delta={onlinePct === null ? "nenhuma tela" : `${onlinePct}% no ar`}
              deltaTone={onlineDeltaColor}
            />
            <KPICard
              label="Último sinal de TV"
              value={lastSignal ? formatHHMM(lastSignal) : "—"}
              delta={lastSignal ? freshnessLabel(lastSignal) : "nenhuma TV conectada"}
              deltaTone="muted"
            />
          </div>

          {/* Card Telas — tabela top 5 */}
          <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]">
            <div className="flex items-center justify-between border-b border-[var(--color-border)] px-[18px] py-[14px]">
              <div>
                <div className="text-sm font-semibold">Telas</div>
                <div className="mt-0.5 text-[11px] text-[var(--color-muted-foreground)]">
                  Estado em tempo real ·{" "}
                  {freshnessLabel(updatedAt)}
                </div>
              </div>
              <a
                href="/stores"
                className="inline-flex h-8 items-center justify-center rounded-md border border-[var(--color-border-strong)] bg-transparent px-3 text-xs font-medium transition-colors hover:bg-[var(--color-secondary)]"
              >
                Ver lojas →
              </a>
            </div>

            <table className="w-full border-collapse text-xs">
              <thead className="bg-[var(--color-table-row-tint)]">
                <tr>
                  <Th>Tela</Th>
                  <Th>Loja</Th>
                  <Th>Grupo</Th>
                  <Th>Prazo</Th>
                  <Th>Status</Th>
                  <Th align="right">Última sync</Th>
                </tr>
              </thead>
              <tbody>
                {tvsAvailable ? (
                  realTvs.slice(0, TABLE_ROWS).map((tv) => {
                    const status = deriveStatus(tv.last_seen_at);
                    const lastSync = tv.last_seen_at
                      ? formatHHMM(new Date(tv.last_seen_at))
                      : "—";
                    return (
                      <tr
                        key={tv.id}
                        className="border-t border-[var(--color-border)]"
                      >
                        <td className="px-[18px] py-[11px] font-mono text-[11px]">
                          {tv.name}
                        </td>
                        <td className="px-[18px] py-[11px]">
                          <span className="font-mono text-[11px] text-[var(--color-muted-foreground)]">
                            {tv.store_code ?? tv.store_id.slice(0, 8)}
                          </span>
                        </td>
                        <td className="px-[18px] py-[11px] text-[11px] text-[var(--color-muted-foreground)]">
                          {tv.tenant_name ?? "—"}
                        </td>
                        <td className="px-[18px] py-[11px] font-mono text-[11px] tabular-nums">
                          <ContentExpiryCell
                            expiresAt={tv.content_expires_at}
                            hasActive={tv.has_active_content}
                          />
                        </td>
                        <td className="px-[18px] py-[11px]">
                          <TVStatusPill status={status} />
                        </td>
                        <td className="px-[18px] py-[11px] text-right font-mono text-[11px] tabular-nums text-[var(--color-muted-foreground)]">
                          {lastSync}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr className="border-t border-[var(--color-border)]">
                    <td
                      colSpan={6}
                      className="px-[18px] py-6 text-center text-xs text-[var(--color-muted-foreground)]"
                    >
                      {tvsQ.isPending
                        ? "Carregando telas…"
                        : "Nenhuma tela cadastrada. As telas aparecem aqui depois do pareamento."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </AppLayout>
    </RequireAuth>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={cn(
        "px-[18px] py-[10px] text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      {children}
    </th>
  );
}

interface KPICardProps {
  label: string;
  value: string;
  delta: string;
  deltaTone: "success" | "muted" | "warn";
}

function KPICard({ label, value, delta, deltaTone }: KPICardProps) {
  const deltaClass =
    deltaTone === "success"
      ? "text-[var(--color-success)]"
      : deltaTone === "warn"
        ? "text-[var(--color-warning)]"
        : "text-[var(--color-muted-foreground)]";
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-[18px] py-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
        {label}
      </div>
      <div className="mt-1.5 font-mono text-[26px] font-semibold tabular-nums tracking-[-0.02em]">
        {value}
      </div>
      <div className={cn("mt-1 font-mono text-[11px]", deltaClass)}>{delta}</div>
    </div>
  );
}

interface TVStatusPillProps {
  status: TVStatus;
}

function TVStatusPill({ status }: TVStatusPillProps) {
  const tone: StatusTone =
    status === "online"
      ? "success"
      : status === "syncing"
        ? "warn"
        : "destructive";
  const label =
    status === "online"
      ? "No ar"
      : status === "syncing"
        ? "Sync..."
        : "Offline";
  return <StatusPill tone={tone}>{label}</StatusPill>;
}

interface ContentExpiryCellProps {
  expiresAt: string | null;
  hasActive: boolean;
}

/**
 * Mostra quanto tempo até a TV ficar sem arte:
 *
 * - Sem playlist ativa → "Sem arte" (destrutivo).
 * - Playlist ativa sem validade → "∞" (muted) — nunca expira.
 * - Playlist com `valid_until` futuro → "Xd Yh" ou "Xh Ymin"
 *   (warning quando < 48h, destrutivo quando < 6h ou já passou).
 */
function ContentExpiryCell({ expiresAt, hasActive }: ContentExpiryCellProps) {
  if (!hasActive) {
    return (
      <span className="text-[var(--color-destructive)]">Sem arte</span>
    );
  }
  if (!expiresAt) {
    return <span className="text-[var(--color-muted-foreground)]">∞</span>;
  }
  const target = new Date(expiresAt).getTime();
  if (Number.isNaN(target)) return <span>—</span>;
  const diffMs = target - Date.now();
  if (diffMs <= 0) {
    return <span className="text-[var(--color-destructive)]">Expirado</span>;
  }
  const totalMin = Math.floor(diffMs / 60_000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin - days * 60 * 24) / 60);
  const mins = totalMin % 60;
  const label =
    days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
  const tone =
    diffMs < 6 * 3600_000
      ? "text-[var(--color-destructive)]"
      : diffMs < 48 * 3600_000
        ? "text-[var(--color-warning)]"
        : "text-[var(--color-foreground)]";
  return <span className={tone}>{label}</span>;
}
